import * as React from 'react';
import { VaiIcon } from '@vaisala/rockhopper-design-tokens';
import './Modal.scss.js';
import { createPortal } from 'react-dom';
import FocusTrap from 'react-focus-lock';
import { Size } from '../../constants.js';
import { Icon } from '../Icon/Icon.js';
import { Paper } from '../Paper/Paper.js';
import { addClass, Bem } from '../../utils/css.js';
import { stopPropagation, wrapEventHandlers } from '../../utils/common.js';
import { useId } from '../../utils/hooks/useId.js';
import { useAnimationStage } from '../../utils/hooks/useAnimationStage.js';
import { useForceUpdate } from '../../utils/hooks/useForceUpdate.js';

const bem = Bem.create("vai-modal");
const Modal = React.forwardRef(function Modal2(props, ref) {
  const {
    dismissOnOverlayClick = true,
    onEscapeKeyDown,
    isOpen,
    showCloseIcon,
    children,
    width = Size.M,
    dataTa = "vai-modal",
    onDismiss,
    trapFocus = true,
    htmlId,
    onKeyDown,
    role = "dialog",
    animated = true,
    className,
    backgroundClassName,
    ...rest
  } = props;
  const id = useId(htmlId, ["modal"]);
  const mountNodeRef = React.useRef(null);
  const portalNodeRef = React.useRef(null);
  const boxNodeRef = React.useRef(null);
  const [mountedAt, setMountedAt] = React.useState(null);
  let ariaProps = {
    role,
    "aria-modal": isOpen
  };
  for (let key in rest) {
    const k = key;
    if (/^(aria)/.test(k)) {
      if (rest[k]) {
        ariaProps[k] = rest[k];
        delete rest[k];
      }
    }
  }
  const update = useForceUpdate();
  React.useEffect(() => {
    if (isOpen) {
      setMountedAt(Date.now());
    } else {
      setMountedAt(null);
    }
  }, [isOpen]);
  const stage = useAnimationStage(isOpen);
  const onSafeDismiss = React.useCallback((e) => {
    if (typeof onDismiss === "function" && mountedAt !== null && mountedAt + 300 < Date.now()) {
      onDismiss();
    }
  }, [mountedAt, onDismiss]);
  React.useEffect(() => {
    if (mountNodeRef.current !== null) {
      const ownerDocument = mountNodeRef.current.ownerDocument;
      portalNodeRef.current = ownerDocument?.createElement("div");
      ownerDocument.body.appendChild(portalNodeRef.current);
      update();
    }
    return () => {
      if (portalNodeRef.current && portalNodeRef.current.ownerDocument) {
        portalNodeRef.current.ownerDocument.body.removeChild(portalNodeRef.current);
      }
    };
  }, [update]);
  React.useEffect(() => {
    let isClickOutside = false;
    let mousedEl = null;
    const handleMouseDown = (e) => {
      isClickOutside = !boxNodeRef.current?.contains(e.target);
      if (isClickOutside) {
        mousedEl = e.target;
      }
    };
    const handleMouseUp = (e) => {
      if (e.target !== mousedEl) {
        isClickOutside = false;
      }
    };
    const handleClick = (_e) => {
      if (isClickOutside) {
        onSafeDismiss();
      }
    };
    if (isOpen && portalNodeRef.current && dismissOnOverlayClick) {
      document.addEventListener("mousedown", handleMouseDown);
      document.addEventListener("mouseup", handleMouseUp);
      document.addEventListener("click", handleClick);
    }
    return () => {
      document.removeEventListener("mousedown", handleMouseDown);
      document.addEventListener("mouseup", handleMouseUp);
      document.removeEventListener("click", handleClick);
    };
  }, [onSafeDismiss, isOpen, dismissOnOverlayClick]);
  const handleKeyDown = React.useCallback((e) => {
    if (e.key === "Escape") {
      if (onEscapeKeyDown) {
        onEscapeKeyDown(e);
      }
      e.stopPropagation();
      onSafeDismiss();
    }
  }, [onEscapeKeyDown, onSafeDismiss]);
  React.useEffect(() => {
    const eventHandler = wrapEventHandlers(handleKeyDown, onKeyDown);
    document.addEventListener("keydown", eventHandler);
    return () => {
      document.removeEventListener("keydown", eventHandler);
    };
  }, [handleKeyDown, onKeyDown]);
  return stage !== "hidden" && portalNodeRef.current ? createPortal(/* @__PURE__ */ React.createElement(FocusTrapWrapper, {
    trapFocus
  }, /* @__PURE__ */ React.createElement("div", {
    id: id.toString(),
    className: addClass(bem.element("background")(stage, !animated && "instant"), backgroundClassName),
    ref,
    "data-ta": dataTa
  }, /* @__PURE__ */ React.createElement("div", {
    className: bem.block(),
    "aria-modal": isOpen
  }, /* @__PURE__ */ React.createElement("div", {
    ...rest,
    ...ariaProps,
    className: addClass(bem.element("box")(width, stage, animated && "animated"), className),
    onClick: stopPropagation,
    ref: boxNodeRef
  }, /* @__PURE__ */ React.createElement(Paper, {
    tabIndex: -1,
    className: bem.element("paper")()
  }, children), showCloseIcon && /* @__PURE__ */ React.createElement("button", {
    className: bem.element("close-icon-button")(),
    onClick: onSafeDismiss,
    "data-ta": `${dataTa}-close`
  }, /* @__PURE__ */ React.createElement(Icon, {
    name: VaiIcon.Cross,
    className: bem.element("close-icon")()
  })))))), portalNodeRef.current) : /* @__PURE__ */ React.createElement("span", {
    ref: mountNodeRef
  });
});
function FocusTrapWrapper({ children, trapFocus }) {
  return trapFocus ? /* @__PURE__ */ React.createElement(FocusTrap, {
    autoFocus: true
  }, children) : /* @__PURE__ */ React.createElement(React.Fragment, null, children);
}

export { Modal };
